// eslint-disable-next-line @typescript-eslint/no-explicit-any
import { Icon } from "@mdi/react";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { LoadingOutlined } from "@ant-design/icons";
import { mdiCalculator, mdiChartBar } from "@mdi/js";
import { Layout, Select, Space, Tabs, Typography } from "antd";
import { LOW_WELL_COUNT_WARNING_THRESHOLD } from "constants/ml.constants";
import { clearError, clearSummary, setError } from "store/features/ml/mlSlice";
import {
  selectFilterScheduled,
  useFilterScheduledStore
} from "stores/filterScheduledStore";
import { selectAttentionWells, selectTxnId, useMapStore } from "stores/map";
import {
  selectMapViewFollowUpOwed,
  useMapViewFollowUpStore
} from "stores/mapViewFollowUpStore";
import styled from "styled-components";
import { arrayOrEmpty } from "utils";
import { formatMlFeatureName } from "utils/ml/formatMlFeatureName";
import { getMlFeatureDisplayName } from "utils/ml/getMlFeatureDisplayName";

import { useUserUnit } from "hooks";

import { EntityKind } from "models/entityKind";

import { EntitiesInGroupModel } from "components/map/contexts/MapContextState";
import useGetGroupedEntities from "components/well-list/hooks/useGetGroupedEntities";

import { RootState } from "../../store/rootReducer";
import formatPropertyName from "../../utils/column/formatPropertyName";
import getGroupByListColumnByProperty from "../../utils/column/getGroupByListColumnByProperty";
import { IconButton } from "../forecasting/Shared";
import {
  FeatureImportanceChart,
  FeatureImportanceChartProps
} from "./FeatureImportanceChart";
import { decisionPlotTransformer } from "./charts/decisionPlotTransformer";
import { shapDependenceTransformer } from "./charts/dependenceTransformer";
import { featureImpactTransformer } from "./charts/featureImpactTransformer";
import { predictedVsActualTransformer } from "./charts/predictedVsActualTransformer";
import { residualTransformer } from "./charts/residualTransformer";
import { shapSummaryTransformer } from "./charts/shapSummaryTransformer";
import { spearmanHeatmapTransformer } from "./charts/spearmanHeatmapTransformer";
import { stripCodeSuffix } from "./charts/utils/labelFormatter";
import { useCalculateImportance } from "./hooks/useCalculateImportance";
import {
  ChartContext,
  FeatureImportanceSummary,
  MlFeature,
  MlTarget,
  NormalizeFeaturePackage,
  UseCalculateImportanceParams,
  WellGroupInfo
} from "./types";
import { getNormalizedByLabel, resolveNormalizationUnit } from "./utils/nameUtils";

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

const TitleContainer = styled.div`
  .ant-typography {
    color: white;
  }
`;

export interface ChartDashboardProps {
  fullscreenChart?: Maybe<string>;
  onChartFullscreen?: (chartKey: Maybe<string>) => void;
  data: FeatureImportanceSummary;
  onDataChange?: (data: FeatureImportanceSummary) => void;
}

const LayoutRoot = styled(Layout)<{ $isFullscreen?: boolean }>`
  ${(props) =>
    props.$isFullscreen
      ? `
    height: 100%;

    .fullscreen-tabs {
      height: 100%;
      display: flex;
      flex-direction: column;

      .ant-tabs-content-holder {
        flex: 1;
        overflow: auto;
      }
    }
  `
      : ``}

  .ant-layout-header {
    line-height: 1.5rem;
    padding: 10px 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: unset;
    flex-wrap: wrap;
    gap: 10px;

    @media screen and (max-width: 1600px) {
      padding: 10px;
    }
  }

  .ant-layout-content {
    padding: ${(props) => (props.$isFullscreen ? "24px" : "16px 24px")};
    overflow: ${(props) => (props.$isFullscreen ? "hidden" : "auto")};
    height: ${(props) => (props.$isFullscreen ? "100%" : "auto")};
    flex: 1;

    @media screen and (max-width: 1600px) {
      padding: 10px;
    }
  }

  .ant-tabs {
    height: ${(props) => (props.$isFullscreen ? "100%" : "auto")};
    display: flex;
    flex-direction: column;
    gap: 24px;

    @media screen and (max-width: 1600px) {
      gap: 10px;
    }
  }

  .ant-tabs-tabpane {
    height: ${(props) => (props.$isFullscreen ? "100%" : "auto")};
    overflow: ${(props) => (props.$isFullscreen ? "hidden" : "auto")};
    display: flex;
  }

  .ant-space {
    height: ${(props) => (props.$isFullscreen ? "100%" : "auto")};
    overflow: ${(props) => (props.$isFullscreen ? "hidden" : "visible")};
    width: 100%;

    @media screen and (max-width: 1600px) {
      gap: 10px !important;
    }
  }
`;

const DecisionPlotTitleWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  max-width: 800px;
  padding: 0 0 2px 2px;
`;

const DecisionPlotTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-grow: 1;
  width: calc(50% - 8px);
  min-width: 100px;
`;

const DecisionPlotTitle = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TabTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

export const ChartDashboard: FC<ChartDashboardProps> = ({
  fullscreenChart,
  onChartFullscreen,
  data: featureImportanceSummary,
  onDataChange
}) => {
  const dispatch = useDispatch();
  const unitType = useUserUnit();
  const categoryList = useSelector((state: RootState) => state.groupBy.categoryList);
  const mlState = useSelector((state: RootState) => state.ml);
  const features = (mlState.inputFeatures ?? []) as MlFeature[];
  const target = mlState.targetFeature as MlTarget;

  const { mutateAsync: calculateImportance, isLoading } = useCalculateImportance();
  const [groupInfo, setGroupInfo] = useState<WellGroupInfo>({});
  const [chartContext, setChartContext] = useState<ChartContext>({
    selectedFeature: null
  } as ChartContext);

  // Derive feature options directly from shap_dependence keys (source of truth from API)
  const dependenceFeatureKeys = useMemo(() => {
    if (!featureImportanceSummary?.shap_dependence) return [];
    return Object.keys(featureImportanceSummary.shap_dependence);
  }, [featureImportanceSummary]);

  const dependenceFeatureOptions = useMemo(
    () =>
      dependenceFeatureKeys.map((key) => {
        const stripped = stripCodeSuffix(key);
        return { label: stripped, value: key };
      }),
    [dependenceFeatureKeys]
  );
  const featureSelection = useCallback(() => {
    return (
      <DecisionPlotTitleWrapper>
        <DecisionPlotTitleContainer>
          <DecisionPlotTitle title="SHAP Dependence">SHAP Dependence</DecisionPlotTitle>
          <Select
            value={chartContext.selectedFeature}
            onChange={(feature) => {
              const dependenceData = featureImportanceSummary?.shap_dependence?.[feature];

              // Auto-select the interaction feature with the highest strength
              let topInteractionFeature: Maybe<string> = null;
              if (dependenceData?.interaction_strengths) {
                const entries = Object.entries(dependenceData.interaction_strengths);
                if (entries.length > 0) {
                  const [highestFeature] = entries.reduce((max, current) =>
                    current[1] > max[1] ? current : max
                  );
                  topInteractionFeature = highestFeature;
                }
              }

              setChartContext({
                ...chartContext,
                selectedFeature: feature,
                interactionFeature: topInteractionFeature
              });
            }}
            options={dependenceFeatureOptions}
            placeholder="Select feature"
          />
        </DecisionPlotTitleContainer>
        <DecisionPlotTitleContainer>
          <DecisionPlotTitle title="Interaction Feature">
            Interaction Feature
          </DecisionPlotTitle>
          <Select
            value={chartContext.interactionFeature}
            onChange={(feature) => {
              setChartContext({
                ...chartContext,
                interactionFeature: feature
              });
            }}
            options={[
              { label: "None", value: null },
              ...dependenceFeatureOptions.filter(
                (opt) => opt.value !== chartContext.selectedFeature
              )
            ]}
            placeholder="Select interaction feature"
          />
        </DecisionPlotTitleContainer>
      </DecisionPlotTitleWrapper>
    );
  }, [
    chartContext?.selectedFeature,
    chartContext?.interactionFeature,
    dependenceFeatureOptions,
    featureImportanceSummary
  ]);

  const summaryData =
    mlState?.featureImportanceSummary as Maybe<FeatureImportanceSummary>;
  const normalizationFeaturesFromSummary = useMemo(
    () => summaryData?.well_tracking_data?.normalizationSummary ?? [],
    [summaryData]
  );

  // Build an effective target whose Unit includes normalization context
  // e.g. "Mbbl" → "Mbbl per 100 ft" when normalized by Hz Length
  const effectiveTarget = useMemo(() => {
    if (!target) return target;
    const normEntry = normalizationFeaturesFromSummary.find(
      (n) => n.ApplyTo?.Property === target?.Property
    );
    if (!normEntry?.NormalizeBy) return target;

    const normUnit = resolveNormalizationUnit(
      normEntry.NormalizeBy,
      unitType,
      categoryList
    );
    const perValue = normEntry.NormalizeBy.Value;
    const perSuffix = [perValue != null ? String(perValue) : "", normUnit]
      .filter(Boolean)
      .join(" ");

    if (!perSuffix) return target;

    return {
      ...target,
      Unit: target.Unit ? `${target.Unit} per ${perSuffix}` : `per ${perSuffix}`
    };
  }, [target, normalizationFeaturesFromSummary, unitType, categoryList]);

  const getTargetNormLabel = useCallback(
    (target: MlTarget) => {
      const normEntry = normalizationFeaturesFromSummary.find(
        (n) => n.ApplyTo?.Property === target?.Property
      );
      return getNormalizedByLabel(normEntry?.NormalizeBy, unitType, categoryList);
    },
    [normalizationFeaturesFromSummary, unitType, categoryList]
  );

  const targetDisplayLabel = getMlFeatureDisplayName(target, categoryList, unitType);

  const plots: FeatureImportanceChartProps[] = useMemo(
    () => [
      {
        key: "feature impact",
        chartType: "FeatureImpact",
        transformer: featureImpactTransformer,
        title: (target) => {
          const displayName = getMlFeatureDisplayName(target, categoryList, unitType);
          const base = `Feature Impact on ${displayName || "Target"}`;
          const label = getTargetNormLabel(target);
          return label ? `${base} ${label}` : base;
        },
        description:
          "This chart shows the average impact that each of the input features have on the predicted value for the target feature. Y-axis shows all the input features in a descending order according to their impact on prediction. X-axis shows the average impact magnitude that each of the input features have in the calculation.",
        height: `${Math.max(450, features.length * 30)}px`
      },
      {
        key: "predictedVsActual",
        chartType: "PredictedVsActual",
        transformer: predictedVsActualTransformer,
        title: (_, summary) => {
          return `Predicted vs Actual Value
      ${summary?.summary?.rsqr ? ` (R²=${summary?.summary?.rsqr})` : ""}`;
        },
        description:
          "This chart shows the relationship between predicted and actual values. Points closer to the diagonal line indicate better predictions."
      },
      {
        key: "residuals",
        chartType: "Residuals",
        transformer: residualTransformer,
        title: () => "Residuals",
        description:
          "This plot visualizes the difference between actual and predicted values (residuals) for each prediction. The X-axis represents the predicted values, while the Y-axis shows the residuals (Actual − Predicted). Ideally, residuals should be randomly scattered around zero without any clear patterns, indicating that the model captures the underlying data structure well and makes unbiased predictions."
      },

      {
        key: "shapSummary",
        chartType: "ShapSummary",
        transformer: shapSummaryTransformer,
        title: () => `SHAP Summary Plot`,
        description:
          "This plot shows how each feature impacts model predictions across all data points. The color represents the feature value (red = high, blue = low), and the position shows how much it pushes the prediction higher or lower.",
        height: `${Math.max(450, features.length * 30)}px`
      },
      {
        key: "shapDependence",
        chartType: "ShapDependence",
        transformer: shapDependenceTransformer,
        title: () => featureSelection(),
        description:
          "These plots show how a feature's impact on predictions changes across different feature values. The x-axis shows the feature value, while the y-axis shows the SHAP value (impact on prediction). Patterns reveal how the relationship between the feature and target varies across the feature's range."
      },
      {
        key: "decision",
        chartType: "Decision",
        transformer: decisionPlotTransformer,
        title: (target) => {
          const displayName = getMlFeatureDisplayName(target, categoryList, unitType);
          const base = `Decision Path for ${displayName || "Target"}`;
          const label = getTargetNormLabel(target);
          return label ? `${base} ${label}` : base;
        },
        description:
          "The SHAP Decision Path Plot visualizes how each feature contributes to an individual prediction by displaying the cumulative effect of SHAP values along the decision path. Starting from the model's base value, each feature's SHAP value is sequentially added, illustrating how the prediction evolves as features are considered. This plot helps users understand the step-by-step impact of features on a specific prediction, making model decisions more transparent and interpretable.",
        height: `${Math.max(450, features.length * 30)}px`
      },
      {
        key: "spearman heatmap",
        chartType: "Correlation",
        transformer: spearmanHeatmapTransformer,
        title: () => `Spearman Correlation Heatmap`,
        description:
          "This heatmap visualizes the Spearman correlation coefficients between all pairs of features (and the target). Each cell shows the strength and direction of the monotonic relationship between two variables, with values ranging from -1 (perfect negative correlation) to 1 (perfect positive correlation). Strong correlations may indicate redundancy or important relationships in your data.",
        height: `${Math.max(450, features.length * 30)}px`
      }
    ],
    [featureSelection, features.length, getTargetNormLabel, categoryList, unitType]
  );
  const hoveredWell = useMapStore(selectAttentionWells) as string[];
  const filterId = useSelector((state: RootState) => state.filter.filterId);
  const isFiltering = useSelector((state: RootState) => state.filter.isFiltering);
  const isFilterScheduled = useFilterScheduledStore(
    selectFilterScheduled(EntityKind.Well)
  );
  // A run that left the map view out is followed by one that narrows it, held
  // until the camera stops, so the filter id is about to change then as well.
  const isMapViewFollowUpOwed = useMapViewFollowUpStore(
    selectMapViewFollowUpOwed(EntityKind.Well)
  );
  // filterId still names the previous filter until the new one lands, so a run
  // started now would score wells the map no longer groups (EVA-269).
  const isFilterPending = isFiltering || isFilterScheduled || isMapViewFollowUpOwed;
  // calculate() reads this after its await, where the closure only has the
  // values from the click.
  const latestFilterRef = useRef({ id: filterId.id, isPending: isFilterPending });
  latestFilterRef.current = { id: filterId.id, isPending: isFilterPending };
  const txnId = useMapStore(selectTxnId);
  const normalizationFeatures =
    mlState.normalizationFeatures as NormalizeFeaturePackage[];

  useEffect(() => {
    setChartContext((prev) => ({ ...prev, hoveredId: arrayOrEmpty(hoveredWell)[0] }));
  }, [hoveredWell]);

  // Update selectedDecisionFeature whenever dependence keys change
  useEffect(() => {
    if (dependenceFeatureKeys.length === 0) {
      return;
    }
    const firstKey = dependenceFeatureKeys[0];
    // Pre-set the interaction feature with the highest strength
    const dependenceData = featureImportanceSummary?.shap_dependence?.[firstKey];
    let topInteractionFeature: Maybe<string> = null;

    if (dependenceData?.interaction_strengths) {
      const entries = Object.entries(dependenceData.interaction_strengths);
      if (entries.length > 0) {
        const [highestFeature] = entries.reduce((max, current) =>
          current[1] > max[1] ? current : max
        );
        topInteractionFeature = highestFeature;
      }
    }

    setChartContext((prev) => ({
      ...prev,
      selectedFeature: firstKey,
      interactionFeature: topInteractionFeature
    }));
  }, [dependenceFeatureKeys, featureImportanceSummary]);

  useGetGroupedEntities(
    txnId?.id ?? null,
    (data: Record<string, EntitiesInGroupModel>) => {
      const allUwis: WellGroupInfo = Object.keys(data).reduce((acc, key) => {
        const group = data[key];
        for (const uwi of Object.keys(group.uwis)) {
          acc[uwi] = group.group;
        }
        return acc;
      }, {} as WellGroupInfo);
      setGroupInfo(allUwis);
    },
    null,
    // ChartDashboard's grouping is independent of the 2D map widget's zoom;
    // don't let the map's fetch policy clear this widget's entity data.
    false
  );

  const ensureFeatureUnits = (feat: MlFeature): MlFeature => {
    if (feat.unit) return feat;
    const col = getGroupByListColumnByProperty(
      categoryList,
      formatPropertyName(feat.Property)
    );
    return col?.units ? { ...feat, unit: col.units } : feat;
  };

  const calculate = async () => {
    const featuresWithUnits = features.map(ensureFeatureUnits);
    const featuresWithFormattedNames = featuresWithUnits.map((feat) =>
      formatMlFeatureName(feat, categoryList)
    );
    const targetWithFormattedName = target
      ? formatMlFeatureName(target, categoryList)
      : target;
    const data = {
      target: targetWithFormattedName,
      features: featuresWithFormattedNames,
      filterId: filterId.id,
      fields: featuresWithFormattedNames.map((feat) => feat.Property),
      normalize: normalizationFeatures.length > 0 ? normalizationFeatures : null
    } as UseCalculateImportanceParams;

    try {
      dispatch(clearError());
      dispatch(clearSummary());
      const result = await calculateImportance(data);
      // The filter moved while the model ran, so the map no longer groups the
      // wells it scored and they would render as n/a.
      const latestFilter = latestFilterRef.current;
      if (latestFilter.id !== data.filterId || latestFilter.isPending) {
        toast.warn(
          "The filter changed during the calculation, so the results were discarded. Please calculate again."
        );
        return;
      }
      onDataChange?.(result);
      if (result?.["well_tracking_data"]?.["droppedWellCount"] > 0) {
        toast.warn(
          `Calculation ran with ${result["well_tracking_data"]["droppedWellCount"]} wells dropped due to invalid data. Please check feature details for more information.`
        );
      }
      const remainingWellCount = result?.well_tracking_data?.remainingWellCount;
      if (
        remainingWellCount != null &&
        remainingWellCount < LOW_WELL_COUNT_WARNING_THRESHOLD
      ) {
        toast.warn(
          `Warning: Only ${remainingWellCount} wells were used in the calculation which may lead to invalid results. Please add more wells to ensure reliable calculations.`
        );
      }
    } catch (err) {
      const response = err.response;
      if (response && response.data) {
        const errorDataObject = response.data;
        dispatch(setError(errorDataObject));
        if (errorDataObject?.error) {
          toast.error(errorDataObject.error);
        }
      } else {
        toast.error("Error calculating feature impact.");
      }
    }
  };
  const handleWellHover = useCallback(
    (id: string) => {
      if (chartContext.hoveredId !== id) {
        setChartContext({ ...chartContext, hoveredId: id });
      }
    },
    [chartContext]
  );

  const targetNormLabel = getTargetNormLabel(target);

  return (
    <LayoutRoot $isFullscreen={!!fullscreenChart}>
      {!fullscreenChart && (
        <Header>
          <TitleContainer>
            <Title level={4} style={{ margin: 0 }}>
              Target: {targetDisplayLabel}
            </Title>
            {targetNormLabel ? (
              <Text
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginTop: "4px",
                  margin: 0,
                  lineHeight: 1.2,
                  marginBottom: "3px",
                  fontWeight: 600
                }}>
                {targetNormLabel}
              </Text>
            ) : null}
            <Text type="secondary">
              Analysis of production data and feature importance
            </Text>
          </TitleContainer>
          <IconButton
            type="primary"
            icon={
              isLoading || isFilterPending ? (
                <LoadingOutlined />
              ) : (
                <Icon path={mdiCalculator} size={1.2} />
              )
            }
            onClick={calculate}
            // antd greys a disabled button; forcing white text there leaves it unreadable.
            style={{
              color: isFilterPending ? undefined : "white",
              minWidth: "150px",
              gap: "5px"
            }}
            loading={isLoading}
            disabled={isFilterPending}
            size="large">
            {isLoading
              ? "Calculating..."
              : isFilterPending
              ? "Loading filter..."
              : "Calculate"}
          </IconButton>
        </Header>
      )}

      <Content>
        <Tabs
          defaultActiveKey="visualizations"
          className={fullscreenChart ? "fullscreen-tabs" : ""}
          tabBarStyle={{ display: fullscreenChart ? "none" : "flex" }}>
          <TabPane
            tab={
              <TabTitle>
                <Icon path={mdiChartBar} size={1.0} />
                Visualizations
              </TabTitle>
            }
            key="visualizations">
            <Space direction="vertical" size="large">
              {plots
                .filter((plot) => !fullscreenChart || plot.chartType === fullscreenChart)
                .map((props) => (
                  <FeatureImportanceChart
                    key={props.key}
                    target={effectiveTarget}
                    data={featureImportanceSummary}
                    mapWellEntities={groupInfo}
                    context={chartContext}
                    onWellHover={handleWellHover}
                    onChartFullscreen={onChartFullscreen}
                    isFullscreen={fullscreenChart === props.chartType}
                    {...props}
                  />
                ))}
            </Space>
          </TabPane>
        </Tabs>
      </Content>
    </LayoutRoot>
  );
};
